import { ProviderType } from "@/types";

/** Front-end metadata for a provider */
export interface ProviderMeta {
  id: ProviderType;
  order?: number;
  name: string;
  description?: string;
  logoUrl: string;
  oauthUrl?: string;
  tokenUrl?: string;
  refreshUrl?: string;
  revokeUrl?: string;
  authType: "oauth" | "token" | "accessKeySecretKey";
  tokenName?: string;
  accessKeyName?: string;
  secretKeyName?: string;
  consoleUrl: string;
  tokenHelpUrl?: string;
  experimental?: boolean;
  compute?: boolean;
  // Customizable options during provisioning
  customizable?: {
    disk?: boolean;
  };
  /** User needs to confirm billing in setup before provisioning */
  confirmBilling?: boolean;
  getManageLink: (id: string) => string | undefined;
  getConsoleLink: (id: string) => string | undefined;
  summary: string[];
}

const providerMeta: Record<ProviderType, ProviderMeta> = {
  do: {
    id: "do",
    order: 1,
    name: "DigitalOcean",
    authType: "oauth",
    confirmBilling: true,
    compute: true,
    oauthUrl: "https://cloud.digitalocean.com/v1/oauth/authorize",
    tokenUrl: "https://cloud.digitalocean.com/v1/oauth/token",
    refreshUrl: "https://cloud.digitalocean.com/v1/oauth/token",
    revokeUrl: "https://cloud.digitalocean.com/v1/oauth/revoke",
    logoUrl: "/assets/provider/do-logo.svg",
    consoleUrl: "https://cloud.digitalocean.com/droplets",
    getManageLink: (id: string) => `https://cloud.digitalocean.com/droplets/${id}`,
    getConsoleLink: (id: string) =>
      `https://cloud.digitalocean.com/droplets/${id}/terminal/ui/?os_user=root`,
    summary: ["Easy setup process", "Optimized for DollarDeploy", "$200 credit for new users"]
  },
  hetzner: {
    id: "hetzner",
    order: 2,
    name: "Hetzner",
    logoUrl: "/assets/provider/hetzner-logo.svg",
    authType: "token",
    compute: true,
    consoleUrl: "https://console.hetzner.cloud/projects",
    tokenHelpUrl: "https://docs.hetzner.com/cloud/api/getting-started/generating-api-token/",
    getManageLink: (id: string) => `https://console.hetzner.cloud/projects/${id}/servers`,
    getConsoleLink: () => undefined,
    summary: ["High performance", "Low cost", "Huge bandwith allocation"]
  },
  vultr: {
    id: "vultr",
    order: 3,
    name: "Vultr",
    logoUrl: "/assets/provider/vultr-logo.svg",
    authType: "token",
    compute: true,
    consoleUrl: "https://my.vultr.com/",
    tokenHelpUrl: "https://docs.vultr.com/platform/other/api/current-user/new-api-key",
    getManageLink: (id: string) => `https://my.vultr.com/subs/?id=${id}`,
    getConsoleLink: () => undefined,
    summary: ["32 global regions", "Predictable pricing", "NVMe storage"]
  },
  datacrunch: {
    id: "datacrunch",
    order: 4,
    name: "Verda Cloud",
    description: "formerly DataCrunch",
    logoUrl: "/assets/provider/datacrunch-logo.svg",
    authType: "accessKeySecretKey",
    accessKeyName: "Client ID",
    secretKeyName: "Client Secret",
    compute: true,
    customizable: {
      disk: true
    },
    consoleUrl: "https://console.verda.com/",
    tokenHelpUrl: "https://api.verda.com/v1/docs#tag/authentication",
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["GPU instances", "High performance", "AI/ML optimized"]
  },
  aws: {
    id: "aws",
    order: 5,
    name: "Amazon Web Services",
    authType: "accessKeySecretKey",
    accessKeyName: "Access Key ID",
    secretKeyName: "Secret Access Key",
    logoUrl: "/assets/provider/aws-logo.svg",
    consoleUrl: "https://console.aws.amazon.com/ec2/home",
    tokenHelpUrl: "https://docs.dollardeploy.com/start-with-aws/",
    experimental: true,
    compute: true,
    customizable: {
      disk: true
    },
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["Flexible", "Reliable and secure", "Highly scalable"]
  },
  backup: {
    id: "backup",
    order: 6,
    name: "Backup Storage",
    authType: "accessKeySecretKey",
    logoUrl: "https://avatars.githubusercontent.com/u/2232217",
    consoleUrl: "https://console.aws.amazon.com/",
    experimental: false,
    // Backup is an object-storage provider, not a compute provider, so it must
    // not appear in any of the compute provider listings in the UI.
    compute: false,
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["S3-compatible storage", "Secure backups", "Flexible configuration"]
  },
  github: {
    id: "github",
    order: 7,
    name: "GitHub",
    authType: "oauth",
    compute: false,
    logoUrl: "https://avatars.githubusercontent.com/u/9919",
    consoleUrl: "https://github.com/settings/installations",
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["GitHub Actions", "GitHub Apps", "GitHub API"]
  }
};

export const getProviderMeta = (id: string) =>
  id in providerMeta ? providerMeta[id as ProviderType] : undefined;

export const getProviders = () =>
  Object.values(providerMeta).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
